import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { ReactComponent as SearchIcon } from 'assets/search-icon.svg';
import countries from 'components/countries';
import Loader from 'components/Loader';
import i18nContext from 'components/i18n-context';
import Button from 'uikit/Button/Button';
import { CheckBox } from 'uikit/CheckBox/CheckBox';
import Input from 'uikit/Input/Input';
import './HighRiskCountriesTab.scss';

const HighRiskCountriesTab = ({ settingsStore }) => {
  const i18n = useContext(i18nContext);
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!settingsStore.isInitialized) {
      settingsStore.getApplicationProperties();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsStore.isInitialized]);

  useEffect(() => {
    if (settingsStore.isInitialized) {
      settingsStore.resetSettingsStore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const allCountries = useMemo(() => {
    const backendCodes = settingsStore.highRiskCountries || [];
    const extraCodes = backendCodes.filter((code) => !countries.includes(code));
    return [...countries, ...extraCodes];
  }, [settingsStore.highRiskCountries]);

  const filteredCountries = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return allCountries;
    return allCountries.filter((code) => {
      const name = i18n.getMessage(code).toLowerCase();
      return code.toLowerCase().includes(query) || name.includes(query);
    });
  }, [searchQuery, allCountries, i18n]);

  const handleToggleCountry = (code) => {
    const current = settingsStore.highRiskCountries || [];
    const updated = current.includes(code) ? current.filter((c) => c !== code) : [...current, code];
    settingsStore.setHighRiskCountries(updated);
  };

  if (!settingsStore.isInitialized) {
    return <Loader className={'application-loader'} />;
  }

  return (
    <div className={'high-risk-countries-wrapper'}>
      <div className={'high-risk-countries-section'}>
        <h3 className={'high-risk-countries-title'}>{i18n.getMessage('settings.highRiskCountries.title')}</h3>
        <p className={'high-risk-countries-description'}>{i18n.getMessage('settings.highRiskCountries.description')}</p>
        <div className={'high-risk-countries-header'}>
          <span className={'high-risk-countries-counter'}>
            {i18n.getMessage('settings.highRiskCountries.selected')}{' '}
            <b>{(settingsStore.highRiskCountries || []).length}</b> {i18n.getMessage('settings.highRiskCountries.of')}{' '}
            <b>{allCountries.length}</b>
          </span>
          <Input
            className={'high-risk-countries-search'}
            placeholder={i18n.getMessage('settings.highRiskCountries.search.placeholder')}
            name={'search_countries'}
            value={searchQuery}
            onChange={({ target: { value } }) => setSearchQuery(value)}
            Icon={SearchIcon}
          />
        </div>
        <div className={'high-risk-countries-grid'}>
          {filteredCountries.map((code) => (
            <CheckBox
              key={code}
              isActive={(settingsStore.highRiskCountries || []).includes(code)}
              isReverseView={true}
              showCheckBox={true}
              value={`${i18n.getMessage(code)} (${code})`}
              onChange={() => handleToggleCountry(code)}
              disabled={settingsStore.isLoading}
            />
          ))}
        </div>
      </div>
      <div className={'high-risk-countries-actions-wrapper'}>
        <Button
          className={'high-risk-countries-button'}
          size={'medium'}
          type={'primary'}
          roleType={'button'}
          onClick={settingsStore.updateHighRiskCountries}
          isDisabled={!settingsStore.isInitialized || settingsStore.isLoading}
        >
          {settingsStore.isLoading ? <Loader /> : i18n.getMessage('settings.highRiskCountries.button.saveChanges')}
        </Button>
      </div>
    </div>
  );
};

HighRiskCountriesTab.propTypes = {
  settingsStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  settingsStore: stores.settingsStore
}))(observer(HighRiskCountriesTab));
